/**
 * Thin helpers for Inspections screen (ZRF-INSP-01).
 */

export function asArray(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.properties)) return payload.properties;
  if (Array.isArray(payload?.inspections)) return payload.inspections;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

export function formatInspectionDate(value) {
  if (!value) return '—';
  try {
    const d = value?.toDate ? value.toDate() : new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  } catch {
    return '—';
  }
}

export function checklistProgress(rooms = []) {
  const items = (rooms || []).flatMap((r) => (Array.isArray(r.items) ? r.items : []));
  const total = items.length;
  const done = items.filter((i) => i.status && i.status !== 'pending').length;
  return { total, done, percent: total ? Math.round((done / total) * 100) : 0 };
}

export function statusColor(status) {
  switch (String(status || '').toLowerCase()) {
    case 'draft':
      return 'default';
    case 'in_progress':
      return 'primary';
    case 'completed':
      return 'success';
    case 'cancelled':
      return 'error';
    default:
      return 'default';
  }
}

export function itemStatusColor(status) {
  switch (String(status || '').toLowerCase()) {
    case 'pass':
      return 'success';
    case 'fail':
      return 'error';
    case 'na':
      return 'default';
    case 'pending':
    default:
      return 'warning';
  }
}

export const INSPECTION_TYPES = [
  { value: 'move_in', label: 'Move-in' },
  { value: 'move_out', label: 'Move-out' },
  { value: 'routine', label: 'Routine' },
  { value: 'custom', label: 'Custom' }
];

export const ITEM_STATUSES = [
  { value: 'pending', label: 'Pending' },
  { value: 'pass', label: 'Pass' },
  { value: 'fail', label: 'Fail' },
  { value: 'na', label: 'N/A' }
];

/** Max chars for optional per-photo notes (ZRF-INSP-06). */
export const MAX_PHOTO_CAPTION = 200;

/** Photo record id from an uploaded photo's `/api/photos/:id/image` link, or null. */
export function photoIdFromUrl(url) {
  const match = /\/api\/photos\/([A-Za-z0-9_-]{1,128})\/image(?:$|[?#])/.exec(String(url || ''));
  return match ? match[1] : null;
}

/** Plain-language reasons from the inspection photo check (ZRF-INSP-AI-01). */
export const PHOTO_ISSUE_LABELS = {
  blurry: 'Blurry',
  too_dark: 'Too dark',
  too_bright: 'Too bright',
  too_far: 'Too far away to see detail',
  too_close: 'Too close to tell where it is',
  obstructed: 'Something is in the way',
  wrong_subject: "Doesn't show this room's checklist items",
  person_or_personal_info: 'A person or personal details are visible'
};

/** photoUrls may be legacy strings or { url, caption }. */
export function photoEntryUrl(entry) {
  if (!entry) return '';
  if (typeof entry === 'string') return entry.trim();
  return String(entry.url || '').trim();
}

export function photoEntryCaption(entry) {
  if (!entry || typeof entry === 'string') return '';
  // Do not trim here — controlled inputs need spaces while typing.
  return String(entry.caption || entry.notes || entry.description || '').slice(
    0,
    MAX_PHOTO_CAPTION
  );
}

export function toPhotoEntry(entryOrUrl, caption = '') {
  const url = typeof entryOrUrl === 'string' ? entryOrUrl.trim() : photoEntryUrl(entryOrUrl);
  if (!url) return null;
  const nextCaption =
    caption !== undefined && caption !== null
      ? String(caption).slice(0, MAX_PHOTO_CAPTION)
      : photoEntryCaption(entryOrUrl);
  const storagePath =
    entryOrUrl && typeof entryOrUrl === 'object' && entryOrUrl.storagePath
      ? String(entryOrUrl.storagePath)
      : null;
  return storagePath ? { url, caption: nextCaption, storagePath } : { url, caption: nextCaption };
}
